import { useEffect, useRef } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  schemaData?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  schemaData,
}: SEOHeadProps) {
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    const siteName = '蜜桃视频';
    const fullTitle = title ? `${title} - ${siteName}` : siteName;
    document.title = fullTitle;

    const setMeta = (selector: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        const isProperty = selector.startsWith('meta[property=');
        const attr = isProperty ? 'property' : 'name';
        const val = selector.match(/['"]([^'"]+)['"]/)?.[1] || '';
        el.setAttribute(attr, val);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('meta[name="description"]', description);
    if (keywords) setMeta('meta[name="keywords"]', keywords);

    const canonical = canonicalUrl || window.location.origin + window.location.pathname;
    let linkEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.setAttribute('rel', 'canonical');
      document.head.appendChild(linkEl);
    }
    linkEl.setAttribute('href', canonical);

    setMeta('meta[property="og:title"]', ogTitle || fullTitle);
    setMeta('meta[property="og:description"]', ogDescription || description);
    setMeta('meta[property="og:type"]', ogType);
    setMeta('meta[property="og:url"]', canonical);
    setMeta('meta[property="og:site_name"]', siteName);
    setMeta('meta[property="og:locale"]', 'zh_CN');
    if (ogImage) setMeta('meta[property="og:image"]', ogImage);

    setMeta('meta[name="twitter:card"]', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', ogTitle || fullTitle);
    setMeta('meta[name="twitter:description"]', ogDescription || description);
    if (ogImage) setMeta('meta[name="twitter:image"]', ogImage);
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogImage, ogType]);

  useEffect(() => {
    if (!schemaData) return;

    const existing = document.querySelector('script[data-schema="page"]');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'page');
    const data = Array.isArray(schemaData) ? { '@graph': schemaData } : schemaData;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    scriptRef.current = script;

    return () => {
      const el = document.querySelector('script[data-schema="page"]');
      if (el) el.remove();
    };
  }, [schemaData]);

  return null;
}